import { restoreVerifiedSession, assertSessionIdentity, loadVerifiedProfile } from './verifiedSession.js';
import { getPendingInvitation, isInvitationForUser } from './invitationContext.js';
import { loadTenant, setAuthClaims } from './shared.jsx';
import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Clock, Users, CheckCircle, AlertCircle, MessageSquare, BarChart3, FileText, Plus, Send, ThumbsUp, ThumbsDown, User, Settings, LogOut, ChevronRight, ChevronDown, ChevronUp, X, Mic, MicOff, Mail, Search, RefreshCw, Trash2, XCircle, ArrowLeft, Loader2, Bell, BellOff, Eye, Download, Filter, Columns, ArrowUpDown, Maximize2, Minimize2, ChevronLeft, ShieldAlert, Heart, Phone, Rocket, ListOrdered, Wrench, Shield } from 'lucide-react';
import { ToastProvider, ToastContext, useToast, CrisisContext, useCrisisAlert, storage, addAuditEntry, calculateWorkingHours, OWL_LOGO, EMAIL_CONFIG, emailService, detectHarmContent, notifyMentorOfHarmDetection, sendNotification, sendPushToUser, VoiceInputButton, useVoiceInput, initFCM, removeFCMToken, notificationPermissionGranted, requestNotificationPermission, CrisisResourceModal, getUrlParams, clearUrlParams, initFirebase, registerServiceWorker, logAnalyticsEvent, setAnalyticsUser, getTenantUsersKey, migrateTenantData, RelationshipsProvider, STRIPE_CONFIG, canCreateConflict, canAccessMentor, getTrialStatus } from './shared.jsx';
import { DisclaimerModal, TermsOfServiceModal, PrivacyPolicyModal, AccessibilityStatementModal } from './components/LegalModals.jsx';
import { TeamMoodOverview, MoodCheckInModal, MoodHistoryWidget, ParticipantMoodOverview } from './components/MoodComponents.jsx';
import ConfidentialityAgreementView from './components/ConfidentialityAgreementView.jsx';
import LoginView from './components/LoginView.jsx';
import UserManagementView from './components/UserManagementView.jsx';
import AdminConflictReport from './components/AdminConflictReport.jsx';
import { DashboardView, StatCard } from './components/DashboardView.jsx';
import { BillingProvider } from './modules/billing/BillingProvider.jsx';
import TenantLifecycleGate from './modules/billing/TenantLifecycleGate.jsx';
import { createStripeAdapter } from './modules/billing/adapters/StripeAdapter.js';
import { ANIMAL_LIST } from './components/AnimalAvatars.jsx';
import MentorPanelView from './components/MentorPanelView.jsx';
import { getConflictAction, ConflictCard } from './components/ConflictUtils.jsx';
import CreateConflictView from './components/CreateConflictView.jsx';
import ReviewProposedChangesView from './components/ReviewProposedChangesView.jsx';
import TermsAcceptanceView from './components/TermsAcceptanceView.jsx';
import ConflictDetailView from './components/ConflictDetailView.jsx';
import SupportDashboard from './components/SupportDashboard.jsx';
import PlanSelectionView from './components/PlanSelectionView.jsx';
import HelpCenter from './modules/help-center/HelpCenter.jsx';
import PwaInstallNudge from './components/PwaInstallNudge.jsx';
import FirebaseCompatAdapter from './modules/help-center/adapters/FirebaseCompatAdapter.js';
import { PushAdapter } from './modules/help-center/HelpCenterConfig.js';
import { TwaProvider, useIsAndroidWrapper, useIsTwaSubscriptionGated } from './TwaContext.jsx';
import { isFromAndroidTwa } from './twa.js';
import AndroidWebOnlyNotice from './components/AndroidWebOnlyNotice.jsx';
import PublicDeletionPage from './components/PublicDeletionPage.jsx';
import PublicUnsubscribePage from './components/PublicUnsubscribePage.jsx';

// ── ArguMentor FAQ content for the Help Center ──
const ARGUMENTOR_FAQS = [
  {
    category: 'Getting Started',
    icon: Rocket,
    questions: [
      { id: 'faq-gs-1', question: 'What is The ArguMentor?', answer: 'The ArguMentor is a structured conflict resolution app. It guides participants through a 5-phase process (Identify & Define, Communicate, Explore Alternatives, Evaluate & Select, and Agree & Implement) to resolve conflicts constructively with mentor oversight.' },
      { id: 'faq-gs-2', question: 'How do I create a new conflict?', answer: 'From the Dashboard, click the "New Conflict" button. You\'ll be guided through naming the conflict, describing the situation, adding participants, and setting response hours. You can also use the AI-assisted "Say It Your Way" feature to help articulate the conflict.' },
      { id: 'faq-gs-3', question: 'How do I invite another participant?', answer: 'When creating or editing a conflict, use the "Add Participant" section to enter the other person\'s email address. They\'ll receive an invitation and can join once they sign in and accept the confidentiality agreement.' },
      { id: 'faq-gs-4', question: 'What is the confidentiality agreement?', answer: 'Before accessing any conflicts, all users must sign a confidentiality agreement. This ensures that everyone involved commits to keeping conflict details private and engaging respectfully throughout the process.' },
    ],
  },
  {
    category: 'The 5-Phase Process',
    icon: ListOrdered,
    questions: [
      { id: 'faq-ph-1', question: 'What happens in Phase 1: Identify & Define?', answer: 'In this phase, each participant restates the conflict in their own words (paraphrasing). The system checks that you\'re genuinely restating the other person\'s perspective rather than arguing your own position. You can also raise challenges for discussion.' },
      { id: 'faq-ph-2', question: 'What happens in Phase 2: Communicate?', answer: 'Participants write "Because" statements to express their feelings and needs. Each person then paraphrases the other\'s statement to show understanding. The phase includes timed response windows and satisfaction voting to ensure both sides feel heard.' },
      { id: 'faq-ph-3', question: 'What happens in Phase 3: Explore Alternatives?', answer: 'Both participants propose possible solutions to the conflict. There\'s no judgment at this stage. The goal is to brainstorm as many alternatives as possible.' },
      { id: 'faq-ph-4', question: 'What happens in Phase 4: Evaluate & Select?', answer: 'Participants rate each proposed alternative. The system helps identify solutions that both parties find acceptable, guiding you toward a mutually agreeable resolution.' },
      { id: 'faq-ph-5', question: 'What happens in Phase 5: Agree & Implement?', answer: 'This final phase has two parts: (a) both participants acknowledge the chosen resolution, and (b) a follow-up check-in where you rate how well the resolution process worked.' },
    ],
  },
  {
    category: 'Roles & Mentors',
    icon: Users,
    questions: [
      { id: 'faq-rl-1', question: 'What does a mentor do?', answer: 'Mentors oversee their mentees\' conflict resolution processes. They can view conflicts, leave inline comments, send messages, and guide participants through each phase. Mentors are assigned through the relationship system, not directly on conflicts.' },
      { id: 'faq-rl-2', question: 'What is an Observer?', answer: 'An Observer (formerly "Fly on the Wall") can view conflicts of mentees connected to their assigned mentor. They have read-only access and cannot participate directly in the conflict process.' },
      { id: 'faq-rl-3', question: 'What is a Global Observer?', answer: 'A Global Observer (formerly "Omniscient") has visibility across all conflicts connected through the Observer chain. This role is typically used by program administrators who need a broad view.' },
      { id: 'faq-rl-4', question: 'How are mentor relationships set up?', answer: 'Mentor-mentee relationships are managed through the Profile modal or by an admin in User Management. A mentor must be connected to a participant for them to have access to that participant\'s conflicts.' },
    ],
  },
  {
    category: 'Features & Tools',
    icon: Wrench,
    questions: [
      { id: 'faq-ft-1', question: 'What is "Say It Your Way"?', answer: 'Say It Your Way is an AI-powered chat assistant that helps you articulate your thoughts. It\'s available during conflict creation and in the Communication phase to help you express your feelings and needs clearly.' },
      { id: 'faq-ft-2', question: 'What are response hours?', answer: 'Response hours define when participants can interact with a conflict. Outside these hours, the conflict is read-only. This prevents late-night escalations and gives everyone time to reflect. Common presets include 9-5 and 5-9.' },
      { id: 'faq-ft-3', question: 'What is Story Mode?', answer: 'Story Mode generates a narrative summary of your conflict resolution journey, complete with animal avatars for each participant. It\'s a fun way to review and share the process while maintaining privacy.' },
      { id: 'faq-ft-4', question: 'Can I export my conflict record?', answer: 'Yes! From the conflict detail page, you can download a complete record of the conflict, including all phases, communications, and the resolution outcome.' },
      { id: 'faq-ft-5', question: 'What do the mood check-ins do?', answer: 'Mood check-ins let you track how you\'re feeling over time. Mentors can see mood trends for their mentees, and the data appears on the calendar view. It\'s a helpful way to notice emotional patterns during conflict resolution.' },
      { id: 'faq-ft-6', question: 'What is Vote Mode?', answer: 'Vote Mode lets you create quick group decisions. Add a subject, list options, and share a join code with up to 6 participants. Everyone rates each option on a 1-5 scale, and the results are tallied automatically. Voting updates in real time. When all participants have voted, the vote closes automatically and everyone sees the results.' },
      { id: 'faq-ft-7', question: 'What are the free Communication Tools?', answer: 'The ArguMentor offers three standalone communication tools accessible from the Dashboard or at app.theargumentor.com/free: Say It Your Way (AI-assisted expression), Mood Journal (daily check-ins with trend tracking), and Vote Mode (group decisions). Free-tier users get limited access with ads; paid tiers unlock higher limits and ad-free use.' },
      { id: 'faq-ft-8', question: 'What do the amber notification dots mean?', answer: 'Amber dots appear on items that need your attention, for example an open vote you haven\'t rated yet, or a vote where new ratings have come in since you last looked. The dots clear automatically once you view the updated item.' },
    ],
  },
  {
    category: 'Account & Privacy',
    icon: Shield,
    questions: [
      { id: 'faq-ap-1', question: 'How do I change my avatar?', answer: 'Open your Profile modal from the Dashboard (click your avatar or name). You can select from a variety of animal avatars that represent you throughout the app.' },
      { id: 'faq-ap-2', question: 'Is my data private?', answer: 'Yes. All conflict data is protected by Firebase security rules requiring authentication. Only participants, their mentors (through relationships), and authorized observers can access conflict details. The confidentiality agreement reinforces this commitment.' },
      { id: 'faq-ap-3', question: 'How do push notifications work?', answer: 'Enable notifications from the Dashboard to receive alerts when there\'s activity on your conflicts, such as new messages, phase changes, and mentor comments. You can disable them anytime through your browser settings.' },
      { id: 'faq-ap-4', question: 'What is a grandfathered account?', answer: 'Users who created their account before the billing system launched are automatically grandfathered into the Personal plan at no cost. This means full access to all features, no ads, and no usage limits, as a thank-you for being an early adopter.' },
      { id: 'faq-ap-5', question: 'How long is my free-tier data kept?', answer: 'Free-tier Communication Tools data (SIYW sessions, mood check-ins, and closed votes) is automatically removed after 30 days. This gives you enough time to spot mood trends and review past votes, while keeping storage manageable. Upgrade to any paid plan to keep your data indefinitely.' },
    ],
  },
];

// ── Help Center config for ArguMentor ──
const HELP_CENTER_CONFIG = {
  appName: 'The ArguMentor',
  primaryColor: '#d97706',
  logoUrl: null,
  faqEnabled: true,
  chatEnabled: true,
  ticketsEnabled: true,
  faqs: ARGUMENTOR_FAQS,
  cannedResponses: [
    { id: 'cr-1', label: 'Greeting', text: 'Hi there! Thanks for reaching out to The ArguMentor support. How can I help you today?' },
    { id: 'cr-2', label: 'Phase Help', text: 'I\'d be happy to walk you through that phase. Which step are you currently on, and what seems unclear?' },
    { id: 'cr-3', label: 'Technical Issue', text: 'I\'m sorry you\'re experiencing a technical issue. Could you describe what you see on screen and what you expected to happen?' },
    { id: 'cr-4', label: 'Mentor Question', text: 'Mentors are connected through the relationship system. You can check your connections in your Profile modal, or ask your admin to set one up for you.' },
    { id: 'cr-5', label: 'Closing', text: 'I\'m glad I could help! If you have any other questions, don\'t hesitate to reach out. Happy resolving!' },
  ],
};

// ── Freemium standalone mode: /free/* paths render the freemium shell ──
// Checked OUTSIDE the main component so no hooks are affected.
const FreemiumShell = React.lazy(() => import('./modules/freemium/FreemiumShell.jsx'));

function TheArgumentorApp() {
  const [currentUser, setCurrentUser] = useState(null);
  const [view, setView] = useState('login');
  const [conflicts, setConflicts] = useState([]);
  const [selectedConflict, setSelectedConflict] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(notificationPermissionGranted());
  const [showGlobalDisclaimer, setShowGlobalDisclaimer] = useState(false);
  const [showTermsOfService, setShowTermsOfService] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showAccessibility, setShowAccessibility] = useState(false);
  const [showMoodCheckIn, setShowMoodCheckIn] = useState(false);
  const [crisisModal, setCrisisModal] = useState({ show: false, type: null });
  // S4: track which tenant support is managing (null = managing own tenant)
  const [viewingTenant, setViewingTenant] = useState(null);
  // Billing: users created before this date are grandfathered (no plan required)
  const BILLING_CUTOFF = '2026-03-13T00:00:00.000Z';
  const [userBilling, setUserBilling] = useState(null); // { plan, planStatus, trialEndsAt, ... }
  const [billingTenant, setBillingTenant] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setBillingTenant(null);
    const tenantId = currentUser?.tenantId;
    if (tenantId && tenantId !== 'tenant-public') {
      loadTenant(tenantId).then(tenant => { if (!cancelled) setBillingTenant(tenant); }).catch(() => {});
    }
    return () => { cancelled = true; };
  }, [currentUser?.tenantId]);
  // Tracks whether the billing lookup for the current user has finished.
  // The dashboard must not paint before this is true: free-plan users get
  // redirected to /free, and rendering first showed them a flash of the
  // full app they don't have access to.
  const [billingResolved, setBillingResolved] = useState(false);
  const isGrandfathered = useCallback((user) => {
    return !user?.createdAt || user.createdAt < BILLING_CUTOFF;
  }, []);
  // PWA install prompt — captured from beforeinstallprompt event
  const [pwaInstallPrompt, setPwaInstallPrompt] = useState(null);
  const [isPwaInstalled, setIsPwaInstalled] = useState(
    typeof window !== 'undefined' && window.matchMedia('(display-mode: standalone)').matches
  );
  const moodCheckScheduledRef = React.useRef(false);
  const loadingRef = React.useRef(true);
  const authResolvedRef = React.useRef(false); // Set true once onAuthStateChanged fires at least once
  // Billing module: stable adapter instance
  const stripeAdapter = React.useMemo(() => createStripeAdapter({ cloudFunctionUrl: STRIPE_CONFIG.cloudFunctionUrl }), []);
  
  // Track previous "next action" text per conflict for change detection
  const prevActionsRef = React.useRef({});
  const [pendingInvite, setPendingInvite] = useState(getPendingInvitation); // Retain invitation through sign-in and checkout

  // ── Help Center adapters (lazy-init after Firebase is ready) ──
  const helpCenterStorageRef = React.useRef(null);
  const helpCenterPushRef = React.useRef(null);
  if (currentUser && window.firebaseDb && !helpCenterStorageRef.current) {
    helpCenterStorageRef.current = new FirebaseCompatAdapter(window.firebaseDb);
    // Custom PushAdapter that uses ArguMentor's sendPushToUser (writes to notifications/ inbox)
    const adapter = new PushAdapter();
    adapter.sendToUser = async (userId, notification) => {
      await sendPushToUser(userId, notification.title || 'Help Center', notification.body || '', notification.data || {});
    };
    // New chat and ticket alerts are sent by the helpCenter* Cloud Functions,
    // which reach super admins and the requester's own org admins.
    adapter.sendToRole = async () => {};
    helpCenterPushRef.current = adapter;
  }

  // PWA install prompt capture + display-mode change detection
  useEffect(() => {
    const handleInstallPrompt = (e) => {
      e.preventDefault();
      setPwaInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleInstallPrompt);

    // Detect when app is installed/uninstalled
    const displayQuery = window.matchMedia('(display-mode: standalone)');
    const handleDisplayChange = (e) => setIsPwaInstalled(e.matches);
    displayQuery.addEventListener('change', handleDisplayChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleInstallPrompt);
      displayQuery.removeEventListener('change', handleDisplayChange);
    };
  }, []);

  // Browser back button support: push history state on view changes
  const viewRef = React.useRef(view);
  const rawSetView = setView;
  // Wrap setView to keep viewRef in sync
  const setViewTracked = (newView) => {
    viewRef.current = newView;
    rawSetView(newView);
  };
  // navigateTo also pushes a history entry for the browser back button
  const navigateTo = (newView) => {
    viewRef.current = newView;
    rawSetView(newView);
    window.history.pushState({ view: newView }, '');
  };

  // Check if user needs plan selection (new user without a plan)
  const checkNeedsPlanSelection = async (userId, createdAt) => {
    // Grandfathered: users created before the billing cutoff skip plan selection
    if (!createdAt || createdAt < BILLING_CUTOFF) return false;
    // Check if user already has a billing record
    try {
      const billing = await storage.get(`stripe/users/${userId}`, true);
      if (billing?.plan) return false; // already has a plan
    } catch (e) { /* if we can't check, don't block */ return false; }
    return true;
  };

  // Load billing data for the current user (used for plan enforcement)
  const loadUserBilling = useCallback(async (user) => {
    const nonParticipant = ['mentor', 'fly-on-wall', 'fly-on-the-wall', 'omniscient'].includes(user?.role);
    if (!user?.id || (isGrandfathered(user) && !nonParticipant)) {
      setUserBilling(null);
      setBillingResolved(true);
      return null;
    }
    try {
      const billing = await storage.get(`stripe/users/${user.id}`, true);
      setUserBilling(billing || null);
      return billing;
    } catch (e) {
      console.warn('[Billing] Failed to load billing:', e.message);
      setUserBilling(null);
      return null;
    } finally {
      setBillingResolved(true);
    }
  }, [isGrandfathered]);

  // Load billing data whenever the current user changes.
  // The dashboard render waits on billingResolved, so this must always
  // settle. The RTDB read can be slow (see the session-restore failsafe
  // above, which budgets tens of seconds), and a read that never settles
  // would otherwise pin the user on a loading screen with no way out.
  // On timeout we fall back to the old behaviour: render the dashboard,
  // and let the free-plan redirect fire if billing arrives later.
  useEffect(() => {
    if (!currentUser?.id) {
      setUserBilling(null);
      setBillingResolved(false);
      return;
    }
    setBillingResolved(false);
    const failsafe = setTimeout(() => {
      console.warn('[Billing] Lookup exceeded 5s, rendering dashboard without it');
      setBillingResolved(true);
    }, 5000);
    loadUserBilling(currentUser).finally(() => clearTimeout(failsafe));
    return () => clearTimeout(failsafe);
  }, [currentUser?.id, loadUserBilling]);

  // Free-plan users belong on /free, not the full dashboard. The redirect
  // fires as soon as billing resolves; the dashboard render below is held
  // until then so free users never see a flash of the full app.
  const redirectingToFree = view === 'dashboard' && billingResolved && userBilling?.plan === 'free' && !new URLSearchParams(window.location.search).has('choosePlan');
  useEffect(() => {
    if (redirectingToFree) {
      window.location.href = '/free';
    }
  }, [redirectingToFree]);

  useEffect(() => {
    // Set initial history state
    window.history.replaceState({ view: 'login' }, '');

    const handlePopState = (event) => {
      const currentView = viewRef.current;
      if (currentView === 'conflict-detail') {
        setSelectedConflict(null);
        viewRef.current = 'dashboard';
        rawSetView('dashboard');
      } else if (currentView === 'create-conflict' || currentView === 'user-management' || currentView === 'mentor-panel' || currentView === 'admin-conflict-report') {
        viewRef.current = 'dashboard';
        rawSetView('dashboard');
      } else {
        // Already at dashboard/login — push state back so we don't leave the app
        window.history.pushState({ view: currentView }, '');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    if (!currentUser || view !== 'dashboard' || !billingResolved) return;
    if (new URLSearchParams(window.location.search).has('choosePlan')) {
      const url = new URL(window.location.href);
      url.searchParams.delete('choosePlan');
      window.history.replaceState(window.history.state, '', url.pathname + url.search);
      setViewTracked('plan-selection');
      return;
    }
    if (!pendingInvite || userBilling?.plan === 'free') return;
    let cancelled = false;
    storage.get('conflict:' + pendingInvite.conflictId).then(conflict => {
      if (!cancelled && isInvitationForUser(pendingInvite, currentUser, conflict)) {
        clearUrlParams();
        setPendingInvite(null);
        setSelectedConflict(conflict);
        setViewTracked('conflict-detail');
      }
    });
    return () => { cancelled = true; };
  }, [currentUser, view, billingResolved, userBilling, pendingInvite]);

  // Firebase real-time listener: auto-sync when ANY user writes conflict data
  // Listens to data/conflicts directly (has .read rule) — ref('data') would fail
  // because the data node itself has no .read rule, only its children do.
  useEffect(() => {
    let unsubscribe = null;
    let cancelled = false;

    const setupRealtimeSync = async () => {
      const fbReady = await initFirebase();
      if (!fbReady || !window.firebaseDb) return;

      if (!currentUser) return;
      try { await assertSessionIdentity(window.firebaseAuth, currentUser); }
      catch { return; }
      if (cancelled) return;

      // Server-scoped refresh also rechecks membership and relationship revocation.

      // Skip the first callback (initial load is handled by loadConflicts)
      let isFirstCallback = true;

      unsubscribe = storage.subscribeConflicts((snapshot) => {
        if (cancelled) return;
        if (isFirstCallback) {
          isFirstCallback = false;
          // Initialize previous action tracking on first load
          if (snapshot.exists()) {
            const conflictsData = snapshot.val();
            if (conflictsData && typeof conflictsData === 'object') {
              Object.values(conflictsData).forEach(c => {
                if (c?.id) prevActionsRef.current[c.id] = { status: c.status, updatedAt: c.updatedAt, updatedBy: c.updatedBy, mentorMessageCount: c.mentorMessages?.length || 0, mentorCommentCount: c.mentorComments?.length || 0 };
              });
            }
          }
          return;
        }

        // Rebuild conflicts array from the snapshot
        const conflictsData = snapshot.exists() ? snapshot.val() : null;
        const updatedConflicts = conflictsData && typeof conflictsData === 'object'
          ? Object.values(conflictsData).filter(c => c !== null)
          : [];

        // === TAKE ACTION NOTIFICATION ===
        // Detect when a conflict's status changes or someone else makes an update
        if (currentUser) {
          for (const conflict of updatedConflicts) {
            if (!conflict?.id) continue;
            const prev = prevActionsRef.current[conflict.id];
            const isMyConflict = conflict.mentees?.some(m =>
              m.id === currentUser.id || m.email?.toLowerCase() === currentUser.email?.toLowerCase()
            ) || conflict.createdBy === currentUser.id;

            if (!isMyConflict) continue;

            const updatedBySomeoneElse = conflict.updatedBy && conflict.updatedBy !== currentUser.id;
            const statusChanged = prev && prev.status !== conflict.status;
            const dataChanged = prev && prev.updatedAt !== conflict.updatedAt && updatedBySomeoneElse;

            if (statusChanged || dataChanged) {
              // Build notification message
              const statusLabels = {
                'identify-define': 'Identify & Define',
                'communicate': 'Communication',
                'explore-alternatives': 'Explore Alternatives',
                'evaluate-select': 'Evaluate & Select',
                'agree-implement': 'Agree & Implement',
                'follow-up': 'Follow Up',
                'resolved': 'Resolved'
              };

              let body = '';
              if (statusChanged) {
                body = `Moved to: ${statusLabels[conflict.status] || conflict.status}`;
              } else {
                // Check for mentor messages/comments
                const prevMentorMsgCount = prev?.mentorMessageCount || 0;
                const currMentorMsgCount = conflict.mentorMessages?.length || 0;
                const prevMentorCommentCount = prev?.mentorCommentCount || 0;
                const currMentorCommentCount = conflict.mentorComments?.length || 0;

                if (currMentorMsgCount > prevMentorMsgCount) {
                  const latestMsg = conflict.mentorMessages[currMentorMsgCount - 1];
                  if (latestMsg?.visibleTo?.includes(currentUser.id) || latestMsg?.userId === currentUser.id) {
                    body = latestMsg.isMentorComment
                      ? `New message from mentor: "${latestMsg.text?.substring(0, 60)}${latestMsg.text?.length > 60 ? '...' : ''}"`
                      : `${latestMsg.userName} replied: "${latestMsg.text?.substring(0, 60)}${latestMsg.text?.length > 60 ? '...' : ''}"`;
                  } else {
                    body = 'A participant has taken action. Your response may be needed.';
                  }
                } else if (currMentorCommentCount > prevMentorCommentCount) {
                  const latestComment = conflict.mentorComments[currMentorCommentCount - 1];
                  if (latestComment?.visibleTo?.includes(currentUser.id) || latestComment?.userId === currentUser.id) {
                    const typeLabel = latestComment.entryType || 'entry';
                    body = `Mentor commented on a ${typeLabel}: "${latestComment.text?.substring(0, 50)}${latestComment.text?.length > 50 ? '...' : ''}"`;
                  } else {
                    body = 'A participant has taken action. Your response may be needed.';
                  }
                } else {
                  body = 'A participant has taken action. Your response may be needed.';
                }
              }

              sendNotification(`${conflict.title || 'Conflict'}: Take Action`, {
                body,
                tag: `conflict-${conflict.id}`,
                data: { conflictId: conflict.id },
                renotify: true
              });
            }

            // Update tracking
            prevActionsRef.current[conflict.id] = {
              status: conflict.status,
              updatedAt: conflict.updatedAt,
              updatedBy: conflict.updatedBy,
              mentorMessageCount: conflict.mentorMessages?.length || 0,
              mentorCommentCount: conflict.mentorComments?.length || 0
            };
          }
        }

        setConflicts(updatedConflicts);

        // If a conflict is currently selected, refresh it too
        setSelectedConflict(prev => {
          if (!prev) return null;
          const freshData = conflictsData?.[prev.id];
          return freshData || null;
        });
      });

    };

    setupRealtimeSync();

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [currentUser]); // Re-attach when user logs in/out

  useEffect(() => {
    // Initialize email service
    emailService.init();
    
    // Register PWA service worker
    registerServiceWorker();
    
    // Listen for service worker messages (e.g., notification click navigation)
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'NAVIGATE_TO_CONFLICT' && event.data.conflictId) {
          // Find and open the conflict
          const conflict = conflicts.find(c => c.id === event.data.conflictId);
          if (conflict) {
            setSelectedConflict(conflict);
            setViewTracked('conflict-detail');
          }
        }
      });
    }
    
    // Check for invitation link in URL
    const { inviteConflictId, inviteEmail } = getUrlParams();
    if (inviteConflictId) {
      setPendingInvite({ conflictId: inviteConflictId, email: inviteEmail });
    }

    // Handle return from Stripe Checkout — clean URL params synchronously
    const billingParam = new URLSearchParams(window.location.search).get('billing');
    if (billingParam) {
      const url = new URL(window.location);
      url.searchParams.delete('billing');
      url.searchParams.delete('tenantId');
      window.history.replaceState({}, '', url.pathname + url.search);
    }
    
    // Wait for Firebase Auth to resolve before loading user data.
    // onAuthStateChanged fires once immediately (with the restored user, or null).
    // This ensures the auth token is ready before we read tenant-scoped RTDB data.
    const initApp = async () => {
      await initFirebase();

      // Org checkout success — force token refresh to pick up new claims (tenantId + admin role)
      // Must run after initFirebase() so firebaseAuth is available
      if (billingParam === 'org-success' && window.firebaseAuth?.currentUser) {
        try {
          await window.firebaseAuth.currentUser.getIdToken(true);
          // Cycle RTDB connection to use the fresh token
          if (window.firebaseDb) {
            const { goOffline, goOnline } = await import('firebase/database');
            goOffline(window.firebaseDb);
            await new Promise(r => setTimeout(r, 300));
            goOnline(window.firebaseDb);
          }
        } catch (e) {
          console.warn('[Billing] Token refresh after org checkout failed:', e);
        }
      }

      if (window.firebaseAuth) {
        await new Promise((resolve) => {
          const unsub = window.firebaseAuth.onAuthStateChanged((firebaseUser) => {
            authResolvedRef.current = true;
            unsub();
            resolve(firebaseUser);
          });
        });
      }

      await loadUserAndConflicts();
    };
    initApp();

    // Failsafe: only force to login if BOTH of these are true after the timeout:
    //   1. We are still in a loading state (loadingRef.current === true)
    //   2. Firebase auth has never resolved (authResolvedRef.current === false)
    // This prevents the timeout from firing during a legitimate (but slow) Google popup flow,
    // where onAuthStateChanged resolves AFTER the popup closes — which can take 20-30s.
    // The timeout is a last resort for true Firebase initialization failures only.
    const timeout = setTimeout(() => {
      if (loadingRef.current && !authResolvedRef.current) {
        console.error('Loading timeout - Firebase auth never resolved, forcing to login screen');
        setLoading(false);
        loadingRef.current = false;
        setViewTracked('login');
      } else if (loadingRef.current) {
        // Auth resolved but loadUserAndConflicts is still running — give it more time.
        // This covers slow Firebase reads after a successful auth.
        const extendedTimeout = setTimeout(() => {
          if (loadingRef.current) {
            console.warn('Extended loading timeout - forcing to login screen');
            setLoading(false);
            loadingRef.current = false;
            setViewTracked('login');
          }
        }, 10000); // 10 more seconds after auth resolves
        return () => clearTimeout(extendedTimeout);
      }
    }, 10000); // 10s for initial auth check (token refresh has its own 5s timeout)
    
    return () => {
      clearTimeout(timeout);
    };
  }, []);

  // Daily mood check-in: show modal if user hasn't checked in today
  // IMPORTANT: Only trigger when user is fully logged in and on the dashboard.
  // This prevents the modal from blocking the login or confidentiality agreement flow.
  useEffect(() => {
    if (!currentUser || view !== 'dashboard' || moodCheckScheduledRef.current) return;
    moodCheckScheduledRef.current = true;

    const checkMoodToday = async () => {
      try {
        const today = new Date().toISOString().split('T')[0];
        const moodData = await storage.get(`mood:${currentUser.id}`, false);
        const checkins = moodData ? JSON.parse(moodData) : {};
        if (!checkins[today]) {
          // Small delay so dashboard renders first
          setTimeout(() => setShowMoodCheckIn(true), 1500);
        }
      } catch (e) {
        console.error('Error checking mood:', e);
      }
    };
    checkMoodToday();

    // Daily 9 AM push notifications are handled server-side by Firebase Cloud Function
    // (sendDailyMoodReminder in /functions/index.js) which runs via Cloud Scheduler.
    // This client-side code only shows the modal when the user opens the app.
  }, [currentUser, view]);

  const loadUserAndConflicts = async () => {
    setLoading(true);
    loadingRef.current = true;
    try {
      const user = await restoreVerifiedSession({
        auth: window.firebaseAuth,
        verifyIdentity: setAuthClaims,
        loadProfile: identity => loadVerifiedProfile({
          auth: window.firebaseAuth,
          identity,
          endpoint: 'https://us-central1-argumentor-ff17a.cloudfunctions.net/getVerifiedAccount',
        }),
      });
      if (user) {
        // Auto-assign avatar to existing users who don't have one, or migrate removed avatars
        if (!user.avatar || user.avatar === 'owl' || user.avatar === 'badger') {
          user.avatar = ANIMAL_LIST[Math.floor(Math.random() * ANIMAL_LIST.length)];
          await storage.set('current-user', user);
          // Update in tenant users list — support users have no tenant so skip
          if (user.role !== 'support' && user.tenantId) {
            const USERS_KEY = getTenantUsersKey(user.tenantId);
            const allUsers = await storage.get(USERS_KEY, true) || [];
            const updatedUsers = allUsers.map(u => u.id === user.id ? { ...u, avatar: user.avatar } : u);
            await storage.set(USERS_KEY, updatedUsers, true);
          }
        }
        setCurrentUser(user);
        // Track login in analytics
        setAnalyticsUser(user.id);
        logAnalyticsEvent('login', { method: user.authProvider || 'email' });
        // Run one-time tenant data migration (non-blocking, idempotent)
        migrateTenantData().catch(e => console.warn('migrateTenantData:', e));
        // Keep localStorage cache in sync
        try { localStorage.setItem('argumentor-current-user', JSON.stringify(user)); } catch(e) {}
        await loadConflicts();

        // Support users bypass the agreement and go to the support dashboard
        if (user.role === 'support') {
          setViewTracked('support-dashboard');
          setLoading(false);
          loadingRef.current = false;
          return;
        }

        // Check agreement + plan selection in parallel
        try {
          const [agreementRecord, needsPlan] = await Promise.all([
            storage.get(`agreement-signed:${user.id}`, true),
            checkNeedsPlanSelection(user.id, user.createdAt),
          ]);
          if (!agreementRecord) {
            setViewTracked('confidentiality-agreement');
          } else if (needsPlan) {
            setViewTracked('plan-selection');
          } else {
            setViewTracked('dashboard');
          }
        } catch (agreementErr) {
          // If we can't check, show agreement to be safe
          setViewTracked('confidentiality-agreement');
        }

        // Initialize FCM for push notifications (non-blocking)
        initFCM(user.id).then(token => {
          // token registered
        });
      } else {
        setCurrentUser(null);
        setConflicts([]);
        setSelectedConflict(null);
        try { localStorage.removeItem('argumentor-current-user'); } catch (e) {}
        setViewTracked('login');
      }
    } catch (error) {
      console.error('[App] Verified session restore failed:', error);
      setCurrentUser(null);
      setConflicts([]);
      setSelectedConflict(null);
      try { localStorage.removeItem('argumentor-current-user'); } catch (e) {}
      setViewTracked('login');
    }
    setLoading(false);
    loadingRef.current = false;
  };

  const loadConflicts = async (retryCount = 0) => {
    try {
      const keys = await storage.list('conflict:', true);

      if (!keys || keys.length === 0) {
        setConflicts([]);
        return;
      }

      const conflictPromises = keys.map(async (key) => {
        try {
          const data = await storage.get(key, true);
          return data;
        } catch (error) {
          console.error(`  -> Error loading conflict ${key}:`, error);
          return null;
        }
      });
      
      const conflictData = await Promise.all(conflictPromises);
      
      const validConflicts = conflictData.filter(c => c !== null);
      setConflicts(validConflicts);
    } catch (error) {
      console.error('ERROR in loadConflicts:', error);
      
      // Retry up to 2 times with exponential backoff
      if (retryCount < 2) {
        const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s
        setTimeout(() => loadConflicts(retryCount + 1), delay);
      } else {
        console.error('Max retries reached, giving up');
        setConflicts([]);
        // Show error to user
        console.error('Unable to load conflicts. Please refresh the page.');
      }
    }
  };

  const handleLogin = async (user) => {
    // Validate before the persistence try/catch so failures cannot fall through.
    await assertSessionIdentity(window.firebaseAuth, user);
    try {
      // Legacy device records may belong to a previous account. They are not
      // the authority for the Firebase session or this verified login.
      // Not awaited: the write waits on the RTDB reconnect that setAuthClaims just forced.
      storage.set('current-user', user)
        .catch(error => console.warn('Unable to update legacy session cache:', error.message));
      // Also cache in localStorage for fast session restore on next visit
      try { localStorage.setItem('argumentor-current-user', JSON.stringify(user)); } catch(e) {}
      setCurrentUser(user);
      
      // Link user to any conflicts where they were invited by email but still have a placeholder ID
      // This runs on EVERY login (not just signup) to catch cases where the user already had an account
      try {
        const conflictKeys = await storage.list('conflict:', true);
        let linkedConflicts = 0;
        
        for (const key of conflictKeys) {
          try {
            const conflict = await storage.get(key, true);
            if (conflict?.mentees) {
              const menteeIndex = conflict.mentees.findIndex(
                m => m.email?.toLowerCase() === user.email.toLowerCase() && m.id !== user.id
              );
              
              if (menteeIndex !== -1) {
                // Update mentee entry with real user ID and name
                conflict.mentees[menteeIndex] = {
                  ...conflict.mentees[menteeIndex],
                  id: user.id,
                  name: user.name
                };
                conflict.updatedAt = new Date().toISOString();
                await storage.set(key, conflict, true);
                linkedConflicts++;
              }
            }
          } catch (conflictErr) {
            console.error(`Error linking conflict ${key}:`, conflictErr);
          }
        }
        
        if (linkedConflicts > 0) {
        }
      } catch (linkErr) {
        console.error('Error during login conflict linking:', linkErr);
        // Don't block login if linking fails
      }
      
      // Always load conflicts before showing dashboard
      await loadConflicts();

      // Support users bypass the agreement and go straight to the support dashboard
      if (user.role === 'support') {
        setViewTracked('support-dashboard');
        return;
      }

      // Check agreement + plan selection in parallel
      try {
        const [agreementRecord, needsPlan] = await Promise.all([
          storage.get(`agreement-signed:${user.id}`, true),
          checkNeedsPlanSelection(user.id, user.createdAt),
        ]);
        if (!agreementRecord) {
          setViewTracked('confidentiality-agreement');
          return;
        }
        if (needsPlan) {
          setViewTracked('plan-selection');
          return;
        }
      } catch (agreementErr) {
        console.error('Error checking agreement:', agreementErr);
        setViewTracked('confidentiality-agreement');
        return;
      }

      // The dashboard effect resumes invitations after current billing resolves.
      setViewTracked('dashboard');
      
      // Initialize FCM for push notifications (non-blocking)
      initFCM(user.id).then(token => {
        // token registered
      });
    } catch (error) {
      console.error('Error saving user:', error);
      // Even if storage fails, still log in for this session
      setCurrentUser(user);
      setViewTracked(user.role === 'support' ? 'support-dashboard' : 'dashboard');
    }
  };

  const handleMoodSubmit = async (rating, details) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const moodData = await storage.get(`mood:${currentUser.id}`, false);
      const checkins = moodData ? JSON.parse(moodData) : {};
      checkins[today] = {
        rating,
        timestamp: new Date().toISOString(),
        userName: currentUser.name,
        ...(details && { details })
      };
      await storage.set(`mood:${currentUser.id}`, JSON.stringify(checkins), false);
      setShowMoodCheckIn(false);
    } catch (e) {
      console.error('Error saving mood:', e);
      setShowMoodCheckIn(false);
    }
  };

  const handleLogout = async () => {
    try {
      // Remove FCM token before logging out
      if (currentUser?.id) {
        await removeFCMToken(currentUser.id);
      }
      // Clear session data BEFORE signing out of Firebase Auth
      // (signing out first would cause PERMISSION_DENIED on the write)
      await storage.set('current-user', null);
    } catch (error) {
      console.warn('Unable to clear legacy session cache:', error.message);
    }
    try {
      // Now sign out of Firebase Auth
      if (window.firebaseAuth) {
        await window.firebaseAuth.signOut();
      }
    } catch (error) {
      console.error('Firebase sign-out failed:', error);
      return;
    }
    try {
      // Also clear local cache
      try { localStorage.removeItem('argumentor-current-user'); } catch(e) {}
      try { localStorage.removeItem('argumentor-email-for-sign-in'); } catch(e) {}
      try { localStorage.removeItem('argumentor-signup-name'); } catch(e) {}
      try { localStorage.removeItem('argumentor-signup-role'); } catch(e) {}
    } catch (error) {
      console.error('Error clearing user:', error);
    }
    setCurrentUser(null);
    setViewTracked('login');
  };

  // TWA gating: true when running inside the Android TWA AND the current
  // user (if any) is not an admin/support role. Admin/support are exempt
  // because their billing actions are tenant management, not consumer purchases.
  // When no user yet (signup time), gating applies whenever in TWA.
  const twaSubGated = useIsTwaSubscriptionGated(currentUser);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-stone-100 flex items-center justify-center">
        <div className="text-stone-600 text-lg font-light">Loading The ArguMentor...</div>
      </div>
    );
  }

  return (
    <ToastProvider>
    <TwaProvider>
    <CrisisContext.Provider value={(type) => setCrisisModal({ show: true, type })}>
    <RelationshipsProvider user={currentUser}>
    <BillingProvider user={currentUser} tenant={billingTenant} billing={userBilling} stripeAdapter={stripeAdapter} isGrandfathered={currentUser ? isGrandfathered(currentUser) : false}>
    <TenantLifecycleGate onLogout={handleLogout} allowAccountManagement={view === 'plan-selection' || view === 'confidentiality-agreement'}>
    <main className="min-h-screen bg-gradient-to-br from-slate-50 to-stone-100">
      <PwaInstallNudge />
      {view === 'login' && <LoginView onLogin={handleLogin} pendingInvite={pendingInvite} />}
      {view === 'support-dashboard' && currentUser && (
        <SupportDashboard
          user={currentUser}
          onLogout={handleLogout}
          onManageUsers={(tenant) => {
            setViewingTenant(tenant);
            navigateTo('user-management');
          }}
        />
      )}
      {view === 'confidentiality-agreement' && currentUser && (
        <ConfidentialityAgreementView
          user={currentUser}
          onAgreed={async () => {
            // Check if new user needs plan selection
            if (await checkNeedsPlanSelection(currentUser?.id, currentUser?.createdAt)) {
              setViewTracked('plan-selection');
              return;
            }
            // Resume the invitation after billing resolves.
            setViewTracked('dashboard');
            // Initialize FCM for push notifications (non-blocking)
            initFCM(currentUser?.id).then(token => {
              // token registered
            });
          }}
          onDecline={handleLogout}
        />
      )}
      {view === 'plan-selection' && currentUser && twaSubGated && (
        <AndroidWebOnlyNotice
          heading="Plans are selected on the web"
          body="To choose or change your subscription plan, sign in at theargumentor.com from a web browser. Your account stays signed in here automatically."
        />
      )}
      {view === 'plan-selection' && currentUser && !twaSubGated && (
        <PlanSelectionView
          user={currentUser}
          cloudFunctionUrl={STRIPE_CONFIG.cloudFunctionUrl}
          authUid={window.firebaseAuth?.currentUser?.uid}
          onPlanSelected={() => {
            // After successful checkout return: route to welcome-return if
            // the user originated from the Android TWA, otherwise dashboard.
            if (isFromAndroidTwa()) {
              setViewTracked('welcome-return');
              return;
            }
            setViewTracked('dashboard');
            initFCM(currentUser?.id).then(() => {});
          }}
          onLogout={handleLogout}
          onSelectFree={async () => {
            // Write a minimal billing record so checkNeedsPlanSelection won't re-trigger
            try {
              await storage.set(`stripe/users/${currentUser.id}`, {
                plan: 'free',
                planStatus: 'active',
                selectedAt: new Date().toISOString(),
              }, true);
            } catch (e) {
              console.warn('[App] Failed to write free plan record:', e);
            }
            // Free users go to the freemium dashboard
            window.location.href = '/free';
          }}
        />
      )}
      {view === 'welcome-return' && (
        <AndroidWebOnlyNotice
          heading="You're all set"
          body="Sign-up is complete. Open the ArguMentor app on your phone. You'll be signed in automatically with your new plan ready to use."
        />
      )}
      {view === 'dashboard' && (!billingResolved || redirectingToFree) && (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-stone-100 flex items-center justify-center">
          <div className="text-stone-600 text-lg font-light" role="status">Loading...</div>
        </div>
      )}
      {view === 'dashboard' && billingResolved && !redirectingToFree && (
        <DashboardView
          user={currentUser} 
          conflicts={conflicts}
          onLogout={handleLogout}
          onSelectConflict={(conflict) => {
            setSelectedConflict(conflict);
            navigateTo('conflict-detail');
          }}
          onCreateConflict={() => navigateTo('create-conflict')}
          userBilling={userBilling}
          isGrandfathered={isGrandfathered(currentUser)}
          pwaInstallPrompt={pwaInstallPrompt}
          isPwaInstalled={isPwaInstalled}
          onInstallPwa={async () => {
            if (pwaInstallPrompt) {
              pwaInstallPrompt.prompt();
              const result = await pwaInstallPrompt.userChoice;
              if (result.outcome === 'accepted') {
                setPwaInstallPrompt(null);
                setIsPwaInstalled(true);
              }
            }
          }}
          onRefresh={loadConflicts}
          onUserManagement={() => navigateTo('user-management')}
          onMentorPanel={() => navigateTo('mentor-panel')}
          onConflictReport={() => navigateTo('admin-conflict-report')}
          notificationsEnabled={notificationsEnabled}
          onToggleNotifications={async () => {
            if (notificationsEnabled) {
              // Can't programmatically revoke, just inform user
              window.alert('To disable notifications, use your browser settings for this site.');
            } else {
              const granted = await requestNotificationPermission();
              setNotificationsEnabled(granted);
              if (granted) {
                // Also initialize FCM if not already done
                initFCM(currentUser?.id).then(token => {
                  // token registered
                });
              } else {
                window.alert('Notification permission was denied. You can enable it in your browser settings.');
              }
            }
          }}
          onUpdateUser={async (updatedUser) => {
            // Support users don't live in a tenant user list
            if (updatedUser.role !== 'support' && updatedUser.tenantId) {
              const USERS_KEY = getTenantUsersKey(updatedUser.tenantId);
              const allUsers = await storage.get(USERS_KEY, true) || [];
              const updatedUsers = allUsers.map(u => u.id === updatedUser.id ? updatedUser : u);
              await storage.set(USERS_KEY, updatedUsers, true);
            }
            setCurrentUser(updatedUser);
            try { localStorage.setItem('argumentor-current-user', JSON.stringify(updatedUser)); } catch(e) {}
          }}
        />
      )}
      {view === 'create-conflict' && (
        <CreateConflictView
          user={currentUser}
          conflicts={conflicts}
          userBilling={userBilling}
          isGrandfathered={isGrandfathered(currentUser)}
          onBack={() => navigateTo('dashboard')}
          onCreated={async () => {
            await loadConflicts();
            navigateTo('dashboard');
          }}
        />
      )}
      {view === 'conflict-detail' && selectedConflict && (
        <ConflictDetailView
          conflict={selectedConflict}
          user={currentUser}
          onLogout={handleLogout}
          onBack={() => {
            setSelectedConflict(null);
            navigateTo('dashboard');
          }}
          onUpdate={async () => {
            try {
              await loadConflicts();
              const updated = await storage.get(`conflict:${selectedConflict.id}`);
              if (updated) {
                setSelectedConflict(updated);
              }
            } catch (error) {
              console.error('Error updating conflict:', error);
              // Still try to reload conflicts even if individual conflict fetch fails
              try {
                await loadConflicts();
              } catch (loadError) {
                console.error('Error reloading conflicts:', loadError);
              }
            }
          }}
        />
      )}
      {view === 'user-management' && currentUser && (currentUser.role === 'admin' || currentUser.role === 'support') && (
        <UserManagementView
          user={currentUser}
          conflicts={conflicts}
          onBack={() => {
            setViewingTenant(null);
            navigateTo(currentUser.role === 'support' ? 'support-dashboard' : 'dashboard');
          }}
          onRefresh={loadConflicts}
          viewingTenantId={viewingTenant?.id || null}
          viewingTenantName={viewingTenant?.name || null}
        />
      )}
      {view === 'admin-conflict-report' && currentUser?.role === 'admin' && (
        <AdminConflictReport
          user={currentUser}
          conflicts={conflicts}
          onBack={() => navigateTo('dashboard')}
          onSelectConflict={(conflict) => {
            setSelectedConflict(conflict);
            navigateTo('conflict-detail');
          }}
        />
      )}
      {view === 'mentor-panel' && currentUser?.role === 'mentor' && (
        <MentorPanelView
          user={currentUser}
          conflicts={conflicts}
          onBack={() => navigateTo('dashboard')}
          onRefresh={loadConflicts}
          onSelectConflict={(conflict) => {
            setSelectedConflict(conflict);
            navigateTo('conflict-detail');
          }}
        />
      )}
      {/* Global Footer — visible on all screens when logged in */}
      {view !== 'login' && (
        <div className="py-3 text-center text-stone-500 text-xs flex flex-wrap justify-center gap-x-1 gap-y-1 px-4">
          <a href="https://www.4hci.com" target="_blank" rel="noopener noreferrer" className="hover:text-amber-700 transition-colors">The ArguMentor, LLC</a>
          <span>·</span>
          <button onClick={() => setShowGlobalDisclaimer(true)} className="hover:text-amber-700 transition-colors">Disclaimer</button>
          <span>·</span>
          <button onClick={() => setShowTermsOfService(true)} className="hover:text-amber-700 transition-colors">Terms of Service</button>
          <span>·</span>
          <button onClick={() => setShowPrivacyPolicy(true)} className="hover:text-amber-700 transition-colors">Privacy Policy</button>
          <span>·</span>
          <button onClick={() => setShowAccessibility(true)} className="hover:text-amber-700 transition-colors">Accessibility</button>
        </div>
      )}
      <DisclaimerModal isOpen={showGlobalDisclaimer} onClose={() => setShowGlobalDisclaimer(false)} />
      <TermsOfServiceModal isOpen={showTermsOfService} onClose={() => setShowTermsOfService(false)} />
      <PrivacyPolicyModal isOpen={showPrivacyPolicy} onClose={() => setShowPrivacyPolicy(false)} />
      <AccessibilityStatementModal isOpen={showAccessibility} onClose={() => setShowAccessibility(false)} />
      {showMoodCheckIn && currentUser && (
        <MoodCheckInModal
          user={currentUser}
          onSubmit={handleMoodSubmit}
          onDismiss={() => setShowMoodCheckIn(false)}
        />
      )}
      <CrisisResourceModal
        isOpen={crisisModal.show}
        onClose={() => setCrisisModal({ show: false, type: null })}
        harmType={crisisModal.type}
      />
      {/* Help Center — floating widget, visible when logged in */}
      {currentUser && helpCenterStorageRef.current && (
        <HelpCenter
          user={{
            id: currentUser.id,
            name: currentUser.name,
            email: currentUser.email,
            role: currentUser.role,
          }}
          config={HELP_CENTER_CONFIG}
          storageAdapter={helpCenterStorageRef.current}
          pushAdapter={helpCenterPushRef.current}
          context={selectedConflict ? { conflictId: selectedConflict.id } : undefined}
          agentRoles={['support', 'admin']}
          defaultTab="faq"
          floating={true}
        />
      )}
    </main>
    </TenantLifecycleGate>
    </BillingProvider>
    </RelationshipsProvider>
    </CrisisContext.Provider>
    </TwaProvider>
    </ToastProvider>
  );
}

// ── Route: freemium paths → FreemiumShell, all else → main app ──
export default function TheArgumentor() {
  // Public account-deletion form (unauthenticated). Detected via URL param,
  // takes precedence over all other routing so users without a login can
  // still request and confirm deletion. Lives outside auth/provider stack.
  const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const action = params?.get('action');
  if (action === 'delete-account' || action === 'confirm-delete') {
    return <PublicDeletionPage />;
  }

  // Unsubscribe link from notification email footers. Like the deletion
  // page, this must work with no login and outside the provider stack.
  if (action === 'unsubscribe') {
    return <PublicUnsubscribePage />;
  }

  if (window.location.pathname.startsWith('/free')) {
    return (
      <TwaProvider>
        <React.Suspense fallback={
          <div className="min-h-screen bg-gradient-to-br from-slate-50 to-stone-100 flex items-center justify-center">
            <div className="text-stone-600 text-lg font-light">Loading...</div>
          </div>
        }>
          <FreemiumShell />
        </React.Suspense>
      </TwaProvider>
    );
  }
  return <TheArgumentorApp />;
}
